import { useState, useEffect } from "react";
import { useMatchGoals } from "./MatchGoalsManager";
import { showToast } from "../../ToastNotification";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import {
  HiTrash,
  HiPlus,
  HiClock,
  HiUser,
  HiShieldCheck,
  HiX,
  HiUserAdd,
  HiArrowLeft,
  HiCheck,
} from "react-icons/hi";
import { FaFutbol } from "react-icons/fa";
import { GiSoccerBall, GiWhistle } from "react-icons/gi";

const MatchGoalsModal = ({ match, selectedSubcategory, onClose }) => {
  const token = useSelector((state) => state.user.token);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  const { goals, addGoal, deleteGoal, players, fetchPlayersByTeam } =
    useMatchGoals(match.id, match, token);

  const [newGoal, setNewGoal] = useState({
    team_id: match.team1_id,
    player_id: "",
    assist_player_id: null,
    goal_type: "normal",
    goal_time_minute: 0,
    goal_time_second: 0,
  });

  const [activeTab, setActiveTab] = useState("add");
  const [quickMinute, setQuickMinute] = useState(null);
  const [showCreatePlayer, setShowCreatePlayer] = useState(false);
  const [creatingPlayer, setCreatingPlayer] = useState(false);

  // Formulario de nuevo jugador (solo nombre y apellido obligatorios)
  const [newPlayer, setNewPlayer] = useState({
    first_name: "",
    last_name: "",
  });
  // Datos de equipos
  const team1Name = match.team1_info?.team_name || `Equipo ${match.team1_id}`;
  const team2Name = match.team2_info?.team_name || `Equipo ${match.team2_id}`;
  const team1Category = match.team1_info?.category;
  const team2Category = match.team2_info?.category;
  const team1Group = match.team1_info?.group_name;
  const team2Group = match.team2_info?.group_name;

  // Cargar jugadores al cambiar de equipo
  useEffect(() => {
    if (!newGoal.team_id || !match?.id) return;

    const teamKey = newGoal.team_id === match.team1_id ? "team1" : "team2";
    const categoryId =
      newGoal.team_id === match.team1_id
        ? match.team1_category_id
        : match.team2_category_id;

    console.log("🔄 Cargando jugadores para:", {
      teamId: newGoal.team_id,
      teamKey,
      categoryId,
    });

    fetchPlayersByTeam(newGoal.team_id, teamKey, categoryId);
  }, [newGoal.team_id, match?.id, showCreatePlayer]); // Agregar match?.id

  const currentTeamPlayers =
    newGoal.team_id === match.team1_id ? players.team1 : players.team2;
  const currentTeamName =
    newGoal.team_id === match.team1_id ? team1Name : team2Name;

  // Crear jugador rápido
  const handleCreatePlayer = async () => {
    if (!newPlayer.first_name.trim() || !newPlayer.last_name.trim()) {
      showToast.error("Nombre y apellido son obligatorios");
      return;
    }

    if (!userId) {
      showToast.error("No se pudo identificar al usuario");
      return;
    }

    setCreatingPlayer(true);
    try {
      // 1. Crear el jugador
      const playerResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/players`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            first_name: newPlayer.first_name.trim(),
            last_name: newPlayer.last_name.trim(),
            user_id: userId,
            player_type: "field_player",
            birth_date: null,
            city: null,
          }),
        },
      );

      if (!playerResponse.ok) {
        const error = await playerResponse.json();
        throw new Error(error.message || "Error al crear jugador");
      }

      const createdPlayer = await playerResponse.json();

      // 2. Asignar el jugador al equipo actual
      const currentTeamId = newGoal.team_id;
      const categoryId =
        newGoal.team_id === match.team1_id
          ? match.team1_category_id
          : match.team2_category_id;

      const assignResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/players/assign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            team_id: currentTeamId,
            player_id: createdPlayer.id,
            team_category_id: categoryId,
          }),
        },
      );

      if (!assignResponse.ok) {
        const error = await assignResponse.json();
        throw new Error(error.message || "Error al asignar jugador al equipo");
      }

      // 3. Seleccionar automáticamente el nuevo jugador
      setNewGoal((prev) => ({
        ...prev,
        player_id: createdPlayer.id.toString(),
      }));

      // 4. Limpiar y volver
      setNewPlayer({ first_name: "", last_name: "" });
      setShowCreatePlayer(false);

      // 5. Recargar jugadores
      const teamKey = newGoal.team_id === match.team1_id ? "team1" : "team2";
      await fetchPlayersByTeam(newGoal.team_id, teamKey, categoryId);

      showToast.success(
        `¡${createdPlayer.first_name} ${createdPlayer.last_name} creado y asignado!`,
      );
    } catch (error) {
      console.error("Error creando jugador:", error);
      showToast.error(error.message || "Error al crear jugador");
    } finally {
      setCreatingPlayer(false);
    }
  };

  // Resto de funciones (handleAddGoal, handleQuickAddGoal, etc.)
  const handleQuickAddGoal = async (minute) => {
    if (!newGoal.player_id) {
      showToast.error("Selecciona un jugador primero");
      return;
    }

    try {
      await addGoal({
        ...newGoal,
        goal_time_minute: minute,
        goal_time_second: 0,
        match_id: match.id,
      });
      setNewGoal((prev) => ({
        ...prev,
        player_id: "",
        assist_player_id: null,
        goal_type: "normal",
        goal_time_minute: "",
        goal_time_second: "",
      }));
      setQuickMinute(null);
      showToast.success(`¡Gol al minuto ${minute}!`);
    } catch (error) {
      console.error("Error adding goal:", error);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoal.player_id) {
      showToast.error("Selecciona un jugador");
      return;
    }
    if (newGoal.goal_time_minute === "" || newGoal.goal_time_minute === null) {
      showToast.error("Ingresa el minuto del gol");
      return;
    }

    try {
      await addGoal({
        ...newGoal,
        match_id: match.id,
        goal_time_minute: parseInt(newGoal.goal_time_minute) || 0,
        goal_time_second: parseInt(newGoal.goal_time_second) || 0,
      });
      setNewGoal((prev) => ({
        ...prev,
        player_id: "",
        assist_player_id: null,
        goal_type: "normal",
        goal_time_minute: "",
        goal_time_second: "",
      }));
      showToast.success("Gol agregado");
    } catch (error) {
      console.error("Error adding goal:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      newGoal.player_id &&
      newGoal.goal_time_minute !== ""
    ) {
      e.preventDefault();
      handleAddGoal();
    }
  };

  // Goles por equipo
  const team1Goals = goals.filter((g) => g.team_id === match.team1_id);
  const team2Goals = goals.filter((g) => g.team_id === match.team2_id);

  const quickMinutes = [
    1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90,
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700 flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FaFutbol className="text-green-400 text-2xl" />
            <div>
              <h2 className="text-lg font-bold text-white">Gestión de Goles</h2>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-blue-400">
                  {team1Name}
                  {team1Category && (
                    <span className="text-gray-400 text-xs ml-1">
                      ({team1Category})
                    </span>
                  )}
                </span>
                <span className="text-gray-500">vs</span>
                <span className="font-semibold text-red-400">
                  {team2Name}
                  {team2Category && (
                    <span className="text-gray-400 text-xs ml-1">
                      ({team2Category})
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors"
          >
            <HiX className="text-gray-400 text-xl" />
          </button>
        </div>

        {/* Marcador rápido */}
        <div className="bg-gray-750 border-b border-gray-700 px-4 py-3">
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <div className="text-sm text-gray-400">{team1Name}</div>
              <div className="text-3xl font-bold text-white">
                {team1Goals.length}
              </div>
            </div>
            <div className="text-gray-500 text-2xl font-bold">-</div>
            <div className="text-center">
              <div className="text-sm text-gray-400">{team2Name}</div>
              <div className="text-3xl font-bold text-white">
                {team2Goals.length}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab("add")}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === "add"
                ? "bg-gray-700 text-white border-b-2 border-green-500"
                : "text-gray-400 hover:text-white hover:bg-gray-700/50"
            }`}
          >
            <HiPlus className="inline mr-1" />
            Agregar Gol
          </button>
          <button
            onClick={() => setActiveTab("list")}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === "list"
                ? "bg-gray-700 text-white border-b-2 border-green-500"
                : "text-gray-400 hover:text-white hover:bg-gray-700/50"
            }`}
          >
            <FaFutbol className="inline mr-1" />
            Goles ({goals.length})
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "add" && (
            <div className="space-y-4">
              {/* Selector de equipo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Equipo que anota
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() =>
                      setNewGoal({
                        ...newGoal,
                        team_id: match.team1_id,
                        player_id: "",
                        assist_player_id: null,
                      })
                    }
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      newGoal.team_id === match.team1_id
                        ? "border-blue-500 bg-blue-500/10 text-white"
                        : "border-gray-600 bg-gray-700/50 text-gray-400 hover:border-gray-500"
                    }`}
                  >
                    <div className="font-semibold text-sm">{team1Name}</div>
                    {team1Category && (
                      <div className="text-xs opacity-75">{team1Category}</div>
                    )}
                  </button>
                  <button
                    onClick={() =>
                      setNewGoal({
                        ...newGoal,
                        team_id: match.team2_id,
                        player_id: "",
                        assist_player_id: null,
                      })
                    }
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      newGoal.team_id === match.team2_id
                        ? "border-red-500 bg-red-500/10 text-white"
                        : "border-gray-600 bg-gray-700/50 text-gray-400 hover:border-gray-500"
                    }`}
                  >
                    <div className="font-semibold text-sm">{team2Name}</div>
                    {team2Category && (
                      <div className="text-xs opacity-75">{team2Category}</div>
                    )}
                  </button>
                </div>
              </div>

              {/* Jugador + botón crear */}
              {!showCreatePlayer ? (
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-300">
                        <HiUser className="inline mr-1" />
                        Jugador *
                      </label>
                      <button
                        onClick={() => setShowCreatePlayer(true)}
                        className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-green-500/10 transition-colors"
                      >
                        <HiUserAdd className="text-base" />
                        Nuevo jugador
                      </button>
                    </div>
                    <select
                      value={newGoal.player_id}
                      onChange={(e) =>
                        setNewGoal({ ...newGoal, player_id: e.target.value })
                      }
                      className="w-full p-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white focus:border-green-500 focus:outline-none transition-colors text-sm"
                    >
                      <option value="">Seleccionar jugador...</option>
                      {currentTeamPlayers?.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.first_name} {player.last_name || ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      <HiUser className="inline mr-1 opacity-50" />
                      Asistencia
                    </label>
                    <select
                      value={newGoal.assist_player_id || ""}
                      onChange={(e) =>
                        setNewGoal({
                          ...newGoal,
                          assist_player_id: e.target.value || null,
                        })
                      }
                      className="w-full p-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white focus:border-green-500 focus:outline-none transition-colors text-sm"
                    >
                      <option value="">Sin asistencia</option>
                      {currentTeamPlayers
                        ?.filter((p) => p.id.toString() !== newGoal.player_id)
                        .map((player) => (
                          <option key={`assist-${player.id}`} value={player.id}>
                            {player.first_name} {player.last_name || ""}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              ) : (
                /* Mini-formulario para crear jugador */
                <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-green-400 flex items-center gap-2">
                      <HiUserAdd />
                      Nuevo jugador para {currentTeamName}
                    </h4>
                    <button
                      onClick={() => setShowCreatePlayer(false)}
                      className="p-1 hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      <HiArrowLeft className="text-gray-400" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        placeholder="Ej: Lionel"
                        value={newPlayer.first_name}
                        onChange={(e) =>
                          setNewPlayer({
                            ...newPlayer,
                            first_name: e.target.value,
                          })
                        }
                        className="w-full p-2.5 bg-gray-600 border border-gray-500 rounded-xl text-white text-sm focus:border-green-500 focus:outline-none transition-colors"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        Apellido *
                      </label>
                      <input
                        type="text"
                        placeholder="Ej: Messi"
                        value={newPlayer.last_name}
                        onChange={(e) =>
                          setNewPlayer({
                            ...newPlayer,
                            last_name: e.target.value,
                          })
                        }
                        className="w-full p-2.5 bg-gray-600 border border-gray-500 rounded-xl text-white text-sm focus:border-green-500 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-gray-400 italic">
                    Se asignará automáticamente a {currentTeamName}. Puedes
                    completar más datos después.
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowCreatePlayer(false);
                        setNewPlayer({ first_name: "", last_name: "" });
                      }}
                      className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-xl text-sm transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleCreatePlayer}
                      disabled={
                        creatingPlayer ||
                        !newPlayer.first_name.trim() ||
                        !newPlayer.last_name.trim()
                      }
                      className="flex-1 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      {creatingPlayer ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Creando...
                        </>
                      ) : (
                        <>
                          <HiCheck className="text-lg" />
                          Crear y Seleccionar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Tipo de gol */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tipo de gol
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: "normal", icon: <FaFutbol />, label: "Normal" },
                    { value: "penalty", icon: <GiWhistle />, label: "Penal" },
                    {
                      value: "free_kick",
                      icon: <GiSoccerBall />,
                      label: "Tiro Libre",
                    },
                    {
                      value: "own_goal",
                      icon: <HiShieldCheck />,
                      label: "Autogol",
                    },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() =>
                        setNewGoal({ ...newGoal, goal_type: type.value })
                      }
                      className={`p-2 rounded-xl border-2 transition-all text-center text-xs ${
                        newGoal.goal_type === type.value
                          ? "border-green-500 bg-green-500/10 text-green-400"
                          : "border-gray-600 bg-gray-700/50 text-gray-400 hover:border-gray-500"
                      }`}
                      title={type.label}
                    >
                      <div className="text-lg mb-1">{type.icon}</div>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Minuto */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <HiClock className="inline mr-1" />
                  Minuto del gol *
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    max="120"
                    placeholder="Min"
                    value={newGoal.goal_time_minute}
                    onChange={(e) =>
                      setNewGoal({
                        ...newGoal,
                        goal_time_minute: e.target.value,
                      })
                    }
                    className="w-24 p-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white text-center focus:border-green-500 focus:outline-none transition-colors"
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    placeholder="Seg"
                    value={newGoal.goal_time_second}
                    onChange={(e) =>
                      setNewGoal({
                        ...newGoal,
                        goal_time_second: e.target.value,
                      })
                    }
                    className="w-20 p-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white text-center focus:border-green-500 focus:outline-none transition-colors"
                  />
                  <button
                    onClick={handleAddGoal}
                    disabled={
                      !newGoal.player_id ||
                      newGoal.goal_time_minute === "" ||
                      players.loading
                    }
                    className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <HiPlus />
                    Agregar Gol
                  </button>
                </div>
              </div>

              {/* Minutos rápidos */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  ⚡ Minutos rápidos
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {quickMinutes.map((minute) => (
                    <button
                      key={minute}
                      onClick={() => {
                        setNewGoal((prev) => ({
                          ...prev,
                          goal_time_minute: minute,
                          goal_time_second: 0,
                        }));
                        setQuickMinute(minute);
                      }}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        quickMinute === minute
                          ? "bg-green-500 text-white"
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      }`}
                    >
                      {minute}'
                    </button>
                  ))}
                </div>
              </div>

              {/* Botón rápido */}
              {quickMinute && newGoal.player_id && (
                <button
                  onClick={() => handleQuickAddGoal(quickMinute)}
                  className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all animate-pulse"
                >
                  ⚡ Agregar Gol al minuto {quickMinute}'
                </button>
              )}
            </div>
          )}

          {/* Lista de goles (igual que antes) */}
          {activeTab === "list" && (
            <div className="space-y-4">
              {/* ... mismo código de lista de goles ... */}
              {goals.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <FaFutbol className="text-4xl mx-auto mb-3 opacity-20" />
                  <p>No hay goles registrados</p>
                  <button
                    onClick={() => setActiveTab("add")}
                    className="mt-2 text-green-400 hover:text-green-300 text-sm"
                  >
                    Agregar primer gol →
                  </button>
                </div>
              ) : (
                <>
                  {team1Goals.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                        {team1Name} ({team1Goals.length})
                      </h3>
                      <div className="space-y-1">
                        {team1Goals
                          .sort(
                            (a, b) =>
                              a.goal_time_minute - b.goal_time_minute ||
                              a.goal_time_second - b.goal_time_second,
                          )
                          .map((goal) => (
                            <GoalItem
                              key={goal.id}
                              goal={goal}
                              onDelete={deleteGoal}
                              color="blue"
                            />
                          ))}
                      </div>
                    </div>
                  )}
                  {team2Goals.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                        {team2Name} ({team2Goals.length})
                      </h3>
                      <div className="space-y-1">
                        {team2Goals
                          .sort(
                            (a, b) =>
                              a.goal_time_minute - b.goal_time_minute ||
                              a.goal_time_second - b.goal_time_second,
                          )
                          .map((goal) => (
                            <GoalItem
                              key={goal.id}
                              goal={goal}
                              onDelete={deleteGoal}
                              color="red"
                            />
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-800 border-t border-gray-700 p-3 flex justify-between items-center">
          <div className="text-xs text-gray-400">
            Presiona Enter para agregar gol rápido
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-xl text-sm transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente GoalItem (igual que antes)
const GoalItem = ({ goal, onDelete, color }) => {
  const goalTypeIcons = {
    normal: <FaFutbol className="text-green-400" />,
    penalty: <GiWhistle className="text-yellow-400" />,
    free_kick: <GiSoccerBall className="text-orange-400" />,
    own_goal: <HiShieldCheck className="text-red-400" />,
  };

  const goalTypeLabels = {
    normal: "",
    penalty: " (P)",
    free_kick: " (TL)",
    own_goal: " (AG)",
  };

  return (
    <div
      className={`group flex items-center justify-between p-2.5 rounded-xl bg-gray-700/50 hover:bg-gray-700 transition-all border-l-2 border-${color}-500`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="text-lg">
          {goalTypeIcons[goal.goal_type] || goalTypeIcons.normal}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-white text-sm truncate">
            {goal.player_name || "Desconocido"}
            <span className="text-xs text-gray-400 ml-1">
              {goalTypeLabels[goal.goal_type]}
            </span>
          </div>
          {goal.assist_player_name && (
            <div className="text-xs text-gray-400 truncate">
              🅰️ {goal.assist_player_name}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono font-bold text-gray-300 bg-gray-800 px-2 py-0.5 rounded">
          {goal.goal_time_minute}'
          {String(goal.goal_time_second || 0).padStart(2, "0")}
        </span>
        <button
          onClick={() => onDelete(goal.id)}
          className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all"
          title="Eliminar gol"
        >
          <HiTrash />
        </button>
      </div>
    </div>
  );
};

export default MatchGoalsModal;
